import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";

import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import {
  IMedicationAdherenceResponse,
  IPatientMedication,
  IPatientMedicationsResponse,
} from "../../@types/Patient";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import { medicationService } from "../../services/medicationService";

export function getPatientMedicationsData(patientId: number) {
  const { data, isFetching, isError, refetch, isRefetching } =
    useQuery<IPatientMedication>({
      queryKey: [`patient-medications-table-${patientId}`, patientId],
      queryFn: async () => {
        if (!patientId) return {} as IPatientMedication;

        const { data } = await medicationService.getMedications(patientId);

        return data as IPatientMedication;
      },
    });

  const patientMedication = data as IPatientMedication;

  return {
    patientMedicationTable: patientMedication?.medications,
    medicationsMaxDate: patientMedication?.maxDate,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

type MedicationsPaginatedResponse = IPaginatedResponse<
  IPatientMedicationsResponse[]
>;

/**
 * Hook for fetching patient medications with infinite scrolling support.
 * Uses TanStack Query's useInfiniteQuery for paginated data fetching.
 *
 * @param patientId - The patient ID to fetch medications for
 * @param pageSize - Number of items per page (default: 20)
 */
export function useInfiniteMedications(
  patientId: number,
  pageSize: number = DEFAULT_PAGE_SIZE
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["patient-medications-infinite", patientId, pageSize],
    queryFn: async ({ pageParam }): Promise<MedicationsPaginatedResponse> => {
      if (!patientId) {
        return {
          result: [],
          currentPage: 1,
          pageSize,
          pages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }

      const response = await medicationService.getMedicationsPaginated(
        patientId,
        pageParam,
        pageSize
      );

      return response.data as MedicationsPaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: MedicationsPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  // Flatten all pages into a single array of medications
  const medications =
    data?.pages.flatMap(
      (page: MedicationsPaginatedResponse) => page.result || []
    ) || [];

  // Get total count from the first page
  const totalCount =
    data?.pages[0]?.pages !== undefined
      ? data.pages[0].pages * pageSize
      : medications.length;

  return {
    medications,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}

/**
 * "Marked as taken" adherence history for one medication. Optional
 * `fromDate`/`toDate` (YYYY-MM-DD) filter the range server-side (the API
 * defaults to the last 30 days); the query refetches whenever they change.
 */
export function useMedicationAdherence(
  patientMedicationId: string,
  fromDate?: string,
  toDate?: string,
  enabled = true
) {
  const { data, error, isError, isFetching, isRefetching, refetch } =
    useQuery<IMedicationAdherenceResponse>({
      queryKey: [
        "medication-adherence",
        patientMedicationId,
        fromDate ?? null,
        toDate ?? null,
      ],
      queryFn: async () =>
        (
          await medicationService.getMedicationAdherence(
            patientMedicationId,
            fromDate,
            toDate
          )
        ).data,
      enabled: enabled && !!patientMedicationId,
    });

  // The endpoint returns 403 when the HCP can't access this patient — surface
  // that distinctly from "no records" (an empty 200).
  const isForbidden =
    axios.isAxiosError(error) && error.response?.status === 403;

  return {
    adherence: data ?? null,
    isError,
    isForbidden,
    isFetching,
    isRefetching,
    refetch,
  };
}
